import type { IdentityType } from "../api/publicMeetingJoinApi";

/**
 * Field validators. Each returns an error message, or "" when the value is valid,
 * so callers can build an error map with `Object.values(errors).some(Boolean)`.
 */

export const required = (value: string, label: string) =>
  value.trim() ? "" : `${label} is required`;

export const email = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "Email is required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "Enter a valid email";
  return "";
};

export const mobile = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "Mobile number is required";
  if (!/^[6-9]\d{9}$/.test(trimmed))
    return "Enter a valid 10-digit mobile number";
  return "";
};

export const meetingId = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "Meeting ID is required";
  if (!/^\d{6,12}$/.test(trimmed)) return "Meeting ID must be 6-12 digits";
  return "";
};

export const caseNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "Case number is required";
  if (trimmed.length > 16) return "Case number must be 16 characters or fewer";
  return "";
};

export const enrollment = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "Enrollment number is required";
  const patterns = [
    /^UP\d{1,7}\/\d{2}$/i, // UP10259/25
    /^[A-Z]{1,5}\/\d{1,10}\/\d{4}$/i, // D/1234/2022, KAR/1234/2021
    /^[A-Z]{1,5}\d{1,10}\/\d{2}$/i, // Other states using XX12345/24
  ];

  if (!patterns.some((pattern) => pattern.test(trimmed)))
    return "Enter a valid enrollment number";
  return "";
};

export const personName = (value: string, label: string) => {
  const trimmed = value.trim();
  if (!trimmed) return `${label} is required`;
  if (trimmed.length < 2) return `${label} must be at least 2 characters`;
  if (!/^[A-Za-z][A-Za-z .'-]*$/.test(trimmed)) {
    return `${label} can only contain letters, spaces, . ' -`;
  }
  return "";
};

export const otp = (value: string, length = 6) => {
  if (!value.trim()) return "OTP is required";
  if (!new RegExp(`^\\d{${length}}$`).test(value))
    return `Enter all ${length} digits`;
  return "";
};

const identityRules: Record<
  IdentityType,
  { label: string; test: RegExp; hint: string }
> = {
  Aadhaar: {
    label: "Aadhaar number",
    test: /^\d{12}$/,
    hint: "Aadhaar number must be 12 digits",
  },
  Voter: {
    label: "Voter ID number",
    test: /^[A-Z]{3}\d{7}$/,
    hint: "Enter valid Voter ID, e.g. ABC1234567",
  },
  PAN: {
    label: "PAN number",
    test: /^[A-Z]{5}\d{4}[A-Z]$/,
    hint: "Enter valid PAN format, e.g. ABCDE1234F",
  },
  Passport: {
    label: "Passport number",
    test: /^[A-Z]\d{7}$/,
    hint: "Enter valid passport number, e.g. A1234567",
  },
  DL: {
    label: "DL number",
    test: /^[A-Z]{2}\d{2}\s?\d{4}\d{7}$/,
    hint: "Enter valid DL number, e.g. DL0120150034761",
  },
};

export const identityNumber = (value: string, type: IdentityType) => {
  const trimmed = value.trim().toUpperCase();
  const rule = identityRules[type];
  if (!trimmed) return `${rule.label} is required`;
  if (!rule.test.test(trimmed)) return rule.hint;
  return "";
};

/** True when every entry in the error map is empty. */
export const isClean = (errors: Record<string, string>) =>
  !Object.values(errors).some(Boolean);
